import { useMemo, useCallback } from 'react'

import { useApp } from '../../../../app/AppContext.jsx'
import { useCatalog } from '../../../catalog/context/CatalogContext.jsx'
import { useProduct } from '../../index.js'

import { useI18n } from '../../../../shared/i18n/useI18n.js'

import { useCompartmentList } from '../../hooks/useCompartmentList.js'
import { buildCompartmentTileSettings, buildAdditionalAreasSection } from '../../utils/compartmentPaneItems.js'
import { getCompartmentListBehavior } from '../../utils/modeRegistry.js'

import { CollapsiblePane } from './CollapsiblePane.jsx'
import { CompartmentsList } from '../CompartmentsList/CompartmentsList.jsx'

// ─────────────────────────────────────────────────────────────────────────────

/**
 * CompartmentsPane — collapsible pane listing all compartments for the active product.
 *
 * Behaviour adapts to the active viewer mode (SELECT / ARRANGE / AXONOMETRIC)
 * via getCompartmentListBehavior. Wires up viewerState / viewerActions from
 * ProductContext so the plan viewer highlights the selected compartment.
 */
export function CompartmentsPane() {
  const { t } = useI18n()
  const { panelSettings } = useApp()
  const { compartments: compartmentTypes } = useCatalog()
  const { selection, capabilities, modes, viewerState, viewerActions } = useProduct()

  const product = selection?.product ?? null
  const activeMode = modes?.activeMode ?? null

  const {
    selectedCompartmentId,
    hoveredCompartmentId,
    selectedLevelId,
    variantsCount,
    selectedVariants,
  } = viewerState

  const {
    setSelectedCompartmentId,
    setHoveredCompartmentId,
    setSelectedLevelId,
    cycleCompartmentVariant,
  } = viewerActions

  const { indoorCompartments, outdoorCompartments } = useCompartmentList({ product, compartmentTypes })
  const compartmentListBehavior = getCompartmentListBehavior(activeMode)

  const tileSettings = useMemo(
    () => buildCompartmentTileSettings(panelSettings, capabilities),
    [panelSettings, capabilities]
  )

  const columns = panelSettings?.unit_sheet_compartments?.tiles_per_row ?? 3

  const additionalAreas = useMemo(
    () => buildAdditionalAreasSection(product, panelSettings, t),
    [product, panelSettings, t]
  )

  const handleSelect = useCallback(
    (number, refTier) => {
      setSelectedCompartmentId(selectedCompartmentId === number ? null : number)
      if (refTier !== null && refTier !== undefined && refTier !== selectedLevelId) {
        setSelectedLevelId(refTier)
      }
    },
    [selectedCompartmentId, selectedLevelId, setSelectedCompartmentId, setSelectedLevelId]
  )

  const handleHover = useCallback(
    (number) => setHoveredCompartmentId(number),
    [setHoveredCompartmentId]
  )

  const handleHoverLeave = useCallback(
    () => setHoveredCompartmentId(null),
    [setHoveredCompartmentId]
  )

  const handleCycleVariant = useCallback(
    (number) => cycleCompartmentVariant(number),
    [cycleCompartmentVariant]
  )

  return (
    <CollapsiblePane
      title={t('product.pane_compartments_title')}
      collapseAriaLabel={t('product.pane_compartments_collapse_aria')}
      expandAriaLabel={t('product.pane_compartments_expand_aria')}
    >
      <CompartmentsList
        indoorCompartments={indoorCompartments}
        outdoorCompartments={outdoorCompartments}
        compartmentListBehavior={compartmentListBehavior}
        compartmentTypes={compartmentTypes}
        columns={columns}
        selectedCompartmentId={selectedCompartmentId}
        hoveredCompartmentId={hoveredCompartmentId}
        variantsCount={variantsCount}
        selectedVariants={selectedVariants}
        tileSettings={tileSettings}
        additionalAreas={additionalAreas}
        onSelect={handleSelect}
        onHover={handleHover}
        onHoverLeave={handleHoverLeave}
        onCycleVariant={handleCycleVariant}
        t={t}
      />
    </CollapsiblePane>
  )
}
